/**
 * Spec 200 — VoiceNote player (Fase 4).
 *
 * Wraps the native `<audio>` element with a Cuadrilla-styled
 * surface plus a speed selector (1× / 1.5× / 2×). The native
 * controls cover seek + play/pause; we don't re-implement those.
 */
import { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';

import './VoiceNotePlayer.css';

interface VoiceNotePlayerProps {
  src: string;
  /** Optional human-readable name; falls back to a relative recorded date. */
  name?: string;
  durationMs?: number;
  recordedAt?: number;
}

const SPEEDS = [1, 1.5, 2] as const;

function formatDuration(ms?: number): string {
  if (!ms || ms <= 0) return '';
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function VoiceNotePlayer({ src, name, durationMs, recordedAt }: VoiceNotePlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [speed, setSpeed] = useState<number>(1);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  const dateLabel = recordedAt
    ? new Date(recordedAt).toLocaleString('es', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <div className="vn-player" data-testid="voice-note-player">
      <div className="vn-meta">
        <Mic size={20} strokeWidth={2} aria-hidden="true" />
        <div className="vn-meta-text">
          <span className="vn-name">{name ?? 'Nota de voz'}</span>
          {(dateLabel || durationMs) && (
            <span className="vn-sub">
              {dateLabel}
              {dateLabel && durationMs ? ' · ' : ''}
              {formatDuration(durationMs)}
            </span>
          )}
        </div>
      </div>
      <audio
        ref={audioRef}
        src={src}
        controls
        preload="metadata"
        className="vn-audio"
        data-testid="voice-note-audio"
      />
      <div className="vn-speed-row">
        <span className="vn-speed-label">Velocidad:</span>
        {SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            className={`vn-speed-btn ${speed === s ? 'vn-speed-btn--active' : ''}`}
            onClick={() => setSpeed(s)}
            data-testid={`voice-note-speed-${s}`}
            aria-label={`Reproducir a ${s} veces la velocidad normal`}
            aria-pressed={speed === s}
          >
            {s}×
          </button>
        ))}
      </div>
    </div>
  );
}
